import type { CareerStep } from '@/types/career-growth';

/** Width of a step card; two dots closer than `cardWidth + cardGap` share a single card. */
const CARD_WIDTH = 160;

/**
 * Every dimension of the career path chart derives from this single block. The SVG and the cards
 * floating over it are laid out in the same pixel space, so a dot and its card can never drift
 * apart: both read their position from the layout computed here.
 */
export const TIMELINE = {
    /** Height of the drawn chart, floating cards included (they sit over the plot). */
    height: 264,
    /**
     * Breathing room kept on both sides of the first and the last year of the axis. A card is
     * centred on its dot, so half a card is what keeps the first and the last card inside the
     * chart instead of clipped by its scrolling edge.
     */
    paddingX: 24,
    /** Top of the plot, where the most recent step sits. */
    plotTop: 30,
    /** Baseline the area closes on, where the earliest step sits. */
    plotBottom: 208,
    /** How many horizontal rules are drawn, `plotTop` and `plotBottom` included. */
    gridLines: 5,
    /**
     * Pixels one year of the axis is worth at the narrowest. A wider panel stretches the axis to
     * fill it rather than leaving the right-hand side empty; the chart only scrolls once the
     * career needs more room than the panel offers.
     */
    yearWidth: 65,
    /** The chart never draws narrower than this, however short the career is. */
    minWidth: 700,
    cardWidth: CARD_WIDTH,
    /** Gap between two cards, and between a card's bottom edge and the dot it belongs to. */
    cardGap: 12,
    /** Room above the plot for the cards, which hang from their bottom edge. */
    cardHeadroom: 0,
    /**
     * How far above its dot a card is anchored. The card floats over the plot rather than above
     * it, so the chart spends its height on the curve instead of on a band reserved for cards.
     */
    cardLift: 56,
    /** Distance between the year graduation's baseline and the bottom of the plot. */
    yearLabelOffset: 30,
    /** Radius of a step dot. */
    dotRadius: 5,
    /** How far past the last real step the target step is anchored, in years. */
    trailingYears: 1.5,
} as const;

/** Years of axis kept after the last plotted step so its card is not clipped by the edge. */
const AXIS_TRAILING_MARGIN = 0.5;

/**
 * A lived step the chart can place: only a dated one has an x. The target is the exception, drawn
 * with or without a horizon.
 */
type DatedCareerStep = CareerStep & { startDate: string };

const isDatedStep = (step: CareerStep): step is DatedCareerStep => step.startDate !== null;

export interface TimelinePoint {
    step: CareerStep;
    x: number;
    y: number;
}

/** One card: a single step, or the steps whose dots are too close to carry a card each. */
export interface TimelineCluster {
    x: number;
    y: number;
    steps: CareerStep[];
}

export interface TimelineLayout {
    /** Drawn width, in pixels: the SVG is drawn 1:1, never stretched by a `viewBox`. */
    width: number;
    minYear: number;
    maxYear: number;
    xForYear: (year: number) => number;
    yearTicks: number[];
    gridLines: number[];
    points: TimelinePoint[];
    /** Empty while a single step leaves nothing to join. */
    linePath: string;
    /** Empty in the same case. */
    areaPath: string;
    /** The dashed segment from the last real step to the target, `null` without a target. */
    targetPath: string | null;
    clusters: TimelineCluster[];
}

/**
 * `2019-07-01` becomes `2019.5`, so two steps started the same year do not stack on one x. The
 * string is read field by field rather than through `Date`, which would parse `YYYY-MM-DD` as
 * UTC midnight and shift the month west of Greenwich.
 */
export const fractionalYear = (iso: string): number => {
    const [year, month] = iso.split('-').map(Number);

    return year + (Number.isFinite(month) ? (month - 1) / 12 : 0);
};

const toPath = (points: TimelinePoint[]): string =>
    points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x} ${point.y}`).join(' ');

/**
 * Where the target sits on the axis. It keeps its own horizon, unless that horizon is so close to
 * the last lived step that the two cards would overlap: `trailingYears` past that step is the
 * floor. A target without a horizon takes the floor itself, and its card says it has no date.
 */
interface PlaceTargetYearParams {
    target: CareerStep;
    lastRealYear: number;
    realStepCount: number;
}

const placeTargetYear = ({
    target,
    lastRealYear,
    realStepCount,
}: PlaceTargetYearParams): number => {
    const floor = lastRealYear + TIMELINE.trailingYears;

    if (target.startDate === null) {
        return floor;
    }

    const horizon = fractionalYear(target.startDate);

    // Alone on the chart, the target has no card to collide with.
    return realStepCount === 0 ? horizon : Math.max(horizon, floor);
};

/**
 * Places the career steps on the chart. Returns `null` when there is neither a dated step nor a
 * target, which is the timeline's empty state. `containerWidth` is the visible width of the
 * scrolling panel, so the axis can fill it instead of stopping short; `0` means "not measured yet"
 * and draws the chart at its narrowest.
 */
export const computeTimelineLayout = (
    steps: CareerStep[],
    today: Date = new Date(),
    containerWidth = 0,
): TimelineLayout | null => {
    const realSteps = steps
        .filter(isDatedStep)
        .filter((step) => step.kind !== 'Target')
        .sort((first, second) => first.startDate.localeCompare(second.startDate));

    // The target comes from the data, never from a position name: one source, one step.
    const targetStep = steps.find((step) => step.kind === 'Target') ?? null;

    if (realSteps.length === 0 && !targetStep) {
        return null;
    }

    const todayYear = today.getFullYear() + today.getMonth() / 12;
    const startYears = realSteps.map((step) => fractionalYear(step.startDate));
    // With no lived step yet, the path starts from today.
    const lastRealYear =
        realSteps.length > 0
            ? Math.max(
                  ...startYears,
                  ...realSteps.map((step) =>
                      step.endDate ? fractionalYear(step.endDate) : todayYear,
                  ),
              )
            : todayYear;

    const targetYear = targetStep
        ? placeTargetYear({
              target: targetStep,
              lastRealYear,
              realStepCount: realSteps.length,
          })
        : null;

    const minYear = Math.floor(
        startYears.length > 0 ? Math.min(...startYears) : Math.min(todayYear, targetYear as number),
    );
    const maxYear = Math.max(
        minYear + 1,
        Math.ceil((targetYear ?? lastRealYear) + AXIS_TRAILING_MARGIN),
    );
    const span = maxYear - minYear;

    const width = Math.max(
        containerWidth,
        TIMELINE.minWidth,
        TIMELINE.paddingX * 2 + span * TIMELINE.yearWidth,
    );
    const plotWidth = width - TIMELINE.paddingX * 2;
    const xForYear = (year: number) => TIMELINE.paddingX + ((year - minYear) / span) * plotWidth;

    const plottedSteps = targetStep ? [...realSteps, targetStep] : realSteps;
    const lastIndex = plottedSteps.length - 1;

    // The path climbs to the right: the earliest step rests on the baseline, the latest on the
    // top of the plot, the others spread evenly in between.
    const yForIndex = (index: number) =>
        lastIndex === 0
            ? (TIMELINE.plotTop + TIMELINE.plotBottom) / 2
            : TIMELINE.plotBottom - (index / lastIndex) * (TIMELINE.plotBottom - TIMELINE.plotTop);

    const points: TimelinePoint[] = plottedSteps.map((step, index) => ({
        step,
        x: xForYear(
            step === targetStep ? (targetYear as number) : fractionalYear(step.startDate as string),
        ),
        y: yForIndex(index),
    }));

    const realPoints = targetStep ? points.slice(0, -1) : points;
    const targetPoint = targetStep ? points[points.length - 1] : null;

    // A current step with no end date keeps the solid line running until today.
    const lastRealPoint = realPoints.at(-1);
    const todayX = xForYear(todayYear);
    const linePoints =
        lastRealPoint &&
        lastRealPoint.step.kind === 'Current' &&
        !lastRealPoint.step.endDate &&
        todayX > lastRealPoint.x
            ? [...realPoints, { ...lastRealPoint, x: todayX }]
            : realPoints;

    const lineEnd = linePoints.at(-1);
    const linePath = linePoints.length > 1 ? toPath(linePoints) : '';
    const areaPath =
        lineEnd && linePoints.length > 1
            ? `${linePath} L${lineEnd.x} ${TIMELINE.plotBottom} L${linePoints[0].x} ${TIMELINE.plotBottom} Z`
            : '';

    const grouped: TimelinePoint[][] = [];
    const minCardDistance = TIMELINE.cardWidth + TIMELINE.cardGap;

    realPoints.forEach((point) => {
        const group = grouped[grouped.length - 1];
        const previous = group?.[group.length - 1];

        if (group && previous && point.x - previous.x < minCardDistance) {
            group.push(point);

            return;
        }

        grouped.push([point]);
    });

    // The target never joins a cluster: it is an intention, not a step already lived.
    if (targetPoint) {
        grouped.push([targetPoint]);
    }

    return {
        width,
        minYear,
        maxYear,
        xForYear,
        yearTicks: Array.from({ length: span + 1 }, (_, index) => minYear + index),
        gridLines: Array.from(
            { length: TIMELINE.gridLines },
            (_, index) =>
                TIMELINE.plotTop +
                (index * (TIMELINE.plotBottom - TIMELINE.plotTop)) / (TIMELINE.gridLines - 1),
        ),
        points,
        linePath,
        areaPath,
        targetPath:
            targetPoint && lineEnd
                ? `M${lineEnd.x} ${lineEnd.y} L${targetPoint.x} ${targetPoint.y}`
                : null,
        clusters: grouped.map((group) => ({
            // The axis no longer reserves half a card on each side, so the card is what is held
            // inside the chart: it slides along its dot rather than clipping on the edge.
            x: Math.min(
                Math.max(
                    group.reduce((sum, point) => sum + point.x, 0) / group.length,
                    TIMELINE.cardWidth / 2,
                ),
                width - TIMELINE.cardWidth / 2,
            ),
            // The card hangs above the highest dot of its group, so it covers none of them.
            y: Math.min(...group.map((point) => point.y)),
            steps: group.map((point) => point.step),
        })),
    };
};
